import { DataGrid } from '@ibsys/kit';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  CustomChartGroupedTablePayload,
  CustomChartTableColumn,
  CustomChartTableRow,
  CustomChartValueType,
} from '@/entities/dashboards';
import { ChartCardShell } from '../ChartCardShell';
import { chartTableHeight, chartTableStyle } from '../chartLayout';
import { buildTableColumns, toIndexedRows, useSortedTableRows } from './chartTable';

type Props = {
  title: string;
  payload: CustomChartGroupedTablePayload;
  /** Тип значений группы: по нему группа-дата показывается датой и упорядочивается по ней же */
  groupType: CustomChartValueType;
  /** Высота диаграммы задана пользователем — таблица прокручивает строки внутри неё */
  hasFixedHeight?: boolean;
};

/** Код колонки группы: тем же именем группа приходит колонкой выборки, в строках её нет. */
const GROUP_COLUMN_CODE = 'group';

/**
 * Таблица с группировкой: строки собраны в группы по первому разрезу.
 *
 * Группа проставляется в свою колонку у каждой строки — так таблица остаётся одной, с общими
 * сортировкой, фильтрами и прокруткой, как остальные таблицы дашборда.
 */
export const GroupedTableChartView: FC<Props> = ({ title, payload, groupType, hasFixedHeight }) => {
  const { t } = useTranslation('qualityDashboard');

  const tableColumns = useMemo(
    (): CustomChartTableColumn[] => [
      { code: GROUP_COLUMN_CODE, title: t('presets.chart.groupColumn'), type: groupType },
      ...payload.columns,
    ],
    [t, groupType, payload.columns],
  );

  const indexedRows = useMemo(
    () =>
      payload.groups.flatMap((group, groupIndex) => {
        const groupRows: CustomChartTableRow[] = group.rows.map((row) => ({
          ...row,
          [GROUP_COLUMN_CODE]: group.title,
        }));
        return toIndexedRows(groupRows, `${groupIndex}-`);
      }),
    [payload.groups],
  );

  const columns = useMemo(
    () =>
      buildTableColumns(
        tableColumns,
        indexedRows.map((row) => row.values),
      ),
    [tableColumns, indexedRows],
  );

  const { rows, sorting, onSortingChange } = useSortedTableRows(indexedRows, tableColumns);

  return (
    <ChartCardShell title={title} scrollable={false}>
      <DataGrid
        columns={columns}
        dataSource={{ mode: 'client', data: rows }}
        getRowId={(row) => row.key}
        enableColumnResizing
        sorting={sorting}
        onSortingChange={onSortingChange}
        height={chartTableHeight(rows.length, hasFixedHeight)}
        style={chartTableStyle(hasFixedHeight)}
      />
    </ChartCardShell>
  );
};
